// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SquareRounded, Timeline } from '@/constants/hardfin-standard-icons';
import { INTERVAL_TYPES, IntervalType } from '@/costs/utils/date-utils';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { InventoryBridgeData } from '@/report/api/types';
import { BridgeReportLoader } from '@/report/components/bridge-report-loader';
import { type BridgeReportType } from '@/report/components/report-loader';
import { ASSET_TRANSITION_PHASES } from '@/report/constants/asset-transition-colors';
import { RollforwardWaterfallDisplayMetricType } from '@/report/types';
import { formatCurrency } from '@/utilities/number';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useRef } from 'react';

interface AssetBridgeChartProps {
  data?: InventoryBridgeData;
  currencyCode?: IsoCurrencyCode;
  isLoading?: boolean;
  isAnimating?: boolean;
  onAnimationComplete?: () => void;
  metricType: RollforwardWaterfallDisplayMetricType;
  periodCount: number;
  reportType: BridgeReportType;
}

interface ChartDataPoint {
  period: string;
  periodLabel: string;
  start: number;
  added: number;
  deployed: number;
  sold: number;
  scrapped: number;
  end: number;
  totalAvailable: number;
  totals: {
    start: Decimal;
    added: Decimal;
    deployed: Decimal;
    sold: Decimal;
    scrapped: Decimal;
    end: Decimal;
    totalAvailable: Decimal;
  };
}

const keys = ['start', 'added', 'deployed', 'sold', 'scrapped'];

export function AssetBridgeChart(props: AssetBridgeChartProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  const margin = { top: 20, right: 20, bottom: 40, left: 80 };
  const width = Math.max(containerSize.width - margin.left - margin.right, 800);
  const height = 400;

  // Show fancy loader while loading or animating
  if (props.isLoading || props.isAnimating) {
    return (
      <Box ref={containerRef} width="100%">
        <BridgeReportLoader
          reportType={props.reportType}
          isDataReady={!props.isLoading}
          onComplete={props.onAnimationComplete}
          periodCount={props.periodCount}
        />
      </Box>
    );
  }

  // Wait for container measurement and data
  if (containerSize.width === 0 || !props.data) {
    return <Box ref={containerRef} width="100%" height={height} />;
  }

  // Helper function to get value based on metric type
  const getValue = (item: { units: number; dollars: string | number }) => {
    if (props.metricType === 'units') {
      return new CurrencyDecimal(item.units);
    }

    return new CurrencyDecimal(item.dollars);
  };

  // Format value for bar labels (absolute values)
  const formatValue = (value: number) => {
    if (props.metricType === 'dollars') {
      return formatCurrency(Math.abs(value), {
        abbreviate: true,
        digits: 1,
        currencyCode: props.currencyCode,
      });
    }

    return Math.abs(value).toLocaleString();
  };

  // Format a total in full for the tooltip (absolute values)
  const formatTooltipValue = (value: Decimal) => {
    if (props.metricType === 'dollars') {
      return props.currencyCode === undefined
        ? formatCurrencyValue(null)
        : formatAmountWithCurrency(value.abs(), props.currencyCode);
    }

    return value.abs().toNumber().toLocaleString();
  };

  // Format value for Y-axis (preserves negative sign)
  const formatAxisValue = (value: number) => {
    if (props.metricType === 'dollars') {
      return formatCurrency(value, {
        abbreviate: true,
        digits: 1,
        currencyCode: props.currencyCode,
      });
    }
    return value.toLocaleString();
  };

  // Get display name for phase
  const getPhaseDisplayName = (key: string) => {
    return (
      ASSET_TRANSITION_PHASES[key as keyof typeof ASSET_TRANSITION_PHASES]
        ?.displayName || key
    );
  };

  // Create comprehensive tooltip content showing full bridge breakdown
  const createTooltipContent = (dataPoint: ChartDataPoint) => {
    // Define bridge flow using existing ASSET_TRANSITION_PHASES and actual data values
    const bridgeFlow: {
      phase: keyof typeof ASSET_TRANSITION_PHASES;
      value: Decimal;
    }[] = [
      { phase: 'start', value: dataPoint.totals.start },
      { phase: 'added', value: dataPoint.totals.added },
      { phase: 'totalAvailable', value: dataPoint.totals.totalAvailable },
      { phase: 'sold', value: dataPoint.totals.sold },
      { phase: 'deployed', value: dataPoint.totals.deployed },
      { phase: 'scrapped', value: dataPoint.totals.scrapped },
      { phase: 'end', value: dataPoint.totals.end },
    ];

    return (
      <Box sx={{ minWidth: 220 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {dataPoint.periodLabel}
        </Typography>
        <Stack spacing={0.25}>
          {bridgeFlow.map((item) => {
            // Show all phases for complete clarity
            const phaseConfig = ASSET_TRANSITION_PHASES[item.phase];
            const showSeparator =
              item.phase === 'totalAvailable' || item.phase === 'end';

            return (
              <Box key={item.phase}>
                {showSeparator && (
                  <Box sx={{ borderTop: 1, borderColor: 'divider', my: 0.5 }} />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'left',
                      fontWeight: showSeparator ? 'bold' : 'normal',
                    }}
                  >
                    {phaseConfig.summaryTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      fontWeight: showSeparator ? 'bold' : 'normal',
                    }}
                  >
                    {formatTooltipValue(item.value)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Format period label based on interval type
  const formatPeriodLabel = (
    periodStart: DateTime,
    interval: IntervalType,
    isLongLabel = false
  ) => {
    switch (interval) {
      case INTERVAL_TYPES.QUARTERLY: {
        return `${String(periodStart.year)}Q${String(periodStart.quarter)}`;
      }
      case INTERVAL_TYPES.MONTHLY: {
        return periodStart.toLocaleString({
          month: isLongLabel ? 'long' : 'short',
          year: 'numeric',
        });
      }
      case INTERVAL_TYPES.WEEKLY: {
        // Show the Sunday date (period start)
        return (
          (isLongLabel ? 'Week of ' : '') +
          periodStart.toLocaleString({
            month: isLongLabel ? 'long' : 'short',
            day: 'numeric',
            year: 'numeric',
          })
        );
      }
      case INTERVAL_TYPES.DAILY: {
        return periodStart.toLocaleString({
          month: isLongLabel ? 'long' : 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
      case INTERVAL_TYPES.YEARLY: {
        return String(periodStart.year);
      }
      default:
        return periodStart.toISODate() ?? '';
    }
  };

  // Transform data for proper stacked bar chart
  const chartData = props.data.periods.map((period) => {
    const rollforward = period.rollforward;

    const startValue = getValue(rollforward.start);
    const addedValue = getValue(rollforward.added);
    const deployedValue = getValue(rollforward.deployed).negated();
    const soldValue = getValue(rollforward.sold).negated();
    const scrappedValue = getValue(rollforward.scrapped).negated();

    // Calculate the end value (net result after all transactions)
    const endValue = startValue
      .plus(addedValue)
      .plus(deployedValue)
      .plus(soldValue)
      .plus(scrappedValue);
    const totalAvailableValue = startValue.plus(addedValue.abs());

    return {
      period: formatPeriodLabel(
        period.periodStart,
        props.data!.interval as IntervalType
      ),
      periodLabel: formatPeriodLabel(
        period.periodStart,
        props.data!.interval as IntervalType,
        true
      ),
      // Positive values (stack upward)
      start: startValue.toNumber(),
      added: addedValue.toNumber(),
      // Negative values (stack downward)
      deployed: deployedValue.toNumber(),
      sold: soldValue.toNumber(),
      scrapped: scrappedValue.toNumber(),
      // Net end value for line chart overlay
      end: endValue.toNumber(),
      totalAvailable: totalAvailableValue.toNumber(),
      totals: {
        start: startValue,
        added: addedValue,
        deployed: deployedValue,
        sold: soldValue,
        scrapped: scrappedValue,
        end: endValue,
        totalAvailable: totalAvailableValue,
      },
    };
  });

  // Use proper asset transition colors
  const colors = scaleOrdinal({
    domain: keys,
    range: [
      ASSET_TRANSITION_PHASES.start.color(theme),
      ASSET_TRANSITION_PHASES.added.color(theme),
      ASSET_TRANSITION_PHASES.deployed.color(theme),
      ASSET_TRANSITION_PHASES.sold.color(theme),
      ASSET_TRANSITION_PHASES.scrapped.color(theme),
    ],
  });

  const yMax = height - margin.top - margin.bottom;

  const xScale = scaleBand({
    domain: chartData.map((d) => d.period),
    range: [0, width],
    padding: 0.2,
  });

  // Calculate the actual stacked ranges to properly size the Y-axis
  let globalMin = 0;
  let globalMax = 0;

  chartData.forEach((dataPoint) => {
    let positiveStackTop = 0;
    let negativeStackBottom = 0;

    keys.forEach((key) => {
      const value = dataPoint[key as keyof ChartDataPoint] as number;
      if (value > 0) {
        positiveStackTop += value;
      } else {
        negativeStackBottom += value;
      }
    });

    globalMax = Math.max(globalMax, positiveStackTop);
    globalMin = Math.min(globalMin, negativeStackBottom);
  });

  // Add padding to prevent collision with axis labels (20% padding)
  const padding = Math.max(Math.abs(globalMax), Math.abs(globalMin)) * 0.1;
  const yScale = scaleLinear({
    domain: [globalMin - padding, globalMax + padding],
    range: [yMax, 0],
  });

  // Calculate stacked bar positions manually for proper positive/negative stacking
  const barWidth = xScale.bandwidth();
  const zeroY = yScale(0);
  const minLabelHeight = 20; // Minimum height to show labels

  return (
    <Box ref={containerRef} width="100%">
      <Stack spacing={2}>
        <Box width="100%">
          <svg
            width={width + margin.left + margin.right}
            height={height + margin.top + margin.bottom}
          >
            <Group left={margin.left} top={margin.top}>
              {/* Zero line */}
              <line
                x1={0}
                x2={width}
                y1={zeroY}
                y2={zeroY}
                stroke={theme.palette.text.secondary}
                strokeWidth={1}
              />

              {chartData.map((dataPoint, periodIndex) => {
                const x = xScale(dataPoint.period) || 0;
                let positiveStackTop = 0;
                let negativeStackBottom = 0;

                return (
                  <Group key={dataPoint.period}>
                    {keys.map((key) => {
                      const value = dataPoint[
                        key as keyof ChartDataPoint
                      ] as number;

                      if (value === 0) return null;

                      let barY: number;
                      let barHeight: number;

                      if (value > 0) {
                        // Positive values stack upward
                        barY = yScale(positiveStackTop + value);
                        barHeight = yScale(positiveStackTop) - barY;
                        positiveStackTop += value;
                      } else {
                        // Negative values stack downward
                        barY = yScale(negativeStackBottom);
                        barHeight = yScale(negativeStackBottom + value) - barY;
                        negativeStackBottom += value;
                      }

                      const absBarHeight = Math.abs(barHeight);
                      const showLabel = absBarHeight >= minLabelHeight;

                      return (
                        <Group key={`${periodIndex}-${key}`}>
                          <Tooltip
                            title={createTooltipContent(dataPoint)}
                            arrow
                            placement="top"
                          >
                            <rect
                              x={x}
                              y={barY}
                              width={barWidth}
                              height={absBarHeight}
                              fill={colors(key)}
                              stroke={theme.palette.background.paper}
                              strokeWidth={0.5}
                              style={{ cursor: 'pointer' }}
                            />
                          </Tooltip>

                          {/* Show label if bar is tall enough */}
                          {showLabel && (
                            <text
                              x={x + barWidth / 2}
                              y={barY + absBarHeight / 2}
                              textAnchor="middle"
                              dy="0.35em"
                              fontSize={10}
                              fill={theme.palette.getContrastText(colors(key))}
                              fontWeight="bold"
                            >
                              {formatValue(value)}
                            </text>
                          )}
                        </Group>
                      );
                    })}
                  </Group>
                );
              })}

              {/* Line chart overlay for end values */}
              <Group>
                {/* Connect end value points with a line */}
                {chartData.length > 1 && (
                  <path
                    d={chartData
                      .map((dataPoint, index) => {
                        const x =
                          (xScale(dataPoint.period) || 0) + barWidth / 2;
                        const y = yScale(dataPoint.end);
                        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                      })
                      .join(' ')}
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    fill="none"
                  />
                )}

                {/* Add circles at each end value point */}
                {chartData.map((dataPoint) => {
                  const x = (xScale(dataPoint.period) || 0) + barWidth / 2;
                  const y = yScale(dataPoint.end);

                  return (
                    <circle
                      key={dataPoint.period}
                      cx={x}
                      cy={y}
                      r={2}
                      fill={theme.palette.primary.main}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Group>

              <AxisBottom
                top={yMax + 10} // Move axis down to avoid overlap
                scale={xScale}
                stroke="transparent" // Remove axis line
                tickStroke="transparent" // Remove tick marks
                tickLabelProps={{
                  fill: theme.palette.text.secondary,
                  fontSize: 12,
                  textAnchor: 'end',
                  angle: -45, // Slant labels
                  dx: '0.5em',
                  dy: '0.25em',
                }}
              />
              <AxisLeft
                scale={yScale}
                tickFormat={(value) => {
                  const numValue = Number(value);
                  // Special handling for zero - no abbreviation or decimals, but still
                  // the selected currency's symbol rather than a hardcoded dollar.
                  if (numValue === 0 && props.metricType === 'dollars') {
                    return formatCurrency(0, {
                      digits: 0,
                      currencyCode: props.currencyCode,
                    });
                  }
                  return formatAxisValue(numValue);
                }}
                stroke={theme.palette.divider}
                tickStroke={theme.palette.text.secondary}
                tickLabelProps={() => ({
                  fill: theme.palette.text.secondary,
                  fontSize: 12,
                  textAnchor: 'end',
                  dy: '0.33em',
                })}
              />
            </Group>
          </svg>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2 }}>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            {keys.map((phase) => (
              <Stack
                key={phase}
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                <SquareRounded fontSize="small" sx={{ fill: colors(phase) }} />
                <Typography variant="body2">
                  {getPhaseDisplayName(phase)}
                </Typography>
              </Stack>
            ))}
            {/* Add legend entry for the ending line */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Timeline
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
              <Typography variant="body2">End</Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
